import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { BarChart3, Users, MousePointer, TrendingUp, Calendar } from 'lucide-react'

interface AbTestClick {
  path: string
  timestamp: string
  userAgent: string
}

interface AbTestResults {
  yesnoClicks: number
  prosConsClicks: number
  totalClicks: number
  yesnoPercentage: number
  prosConsPercentage: number
  recentClicks: AbTestClick[]
}

export const AbTestResultsPage: React.FC = () => {
  const [results, setResults] = useState<AbTestResults>({
    yesnoClicks: 0,
    prosConsClicks: 0,
    totalClicks: 0,
    yesnoPercentage: 0,
    prosConsPercentage: 0,
    recentClicks: []
  })

  useEffect(() => {
    loadAbTestResults()
  }, [])

  const loadAbTestResults = () => {
    try {
      const clicks: AbTestClick[] = JSON.parse(localStorage.getItem('abTestClicks') || '[]')
      const yesnoClicks = clicks.filter(click => click.path === '/tools/yesno-picker').length
      const prosConsClicks = clicks.filter(click => click.path === '/decision-analysis/pros-cons').length
      const totalClicks = clicks.length
      
      const yesnoPercentage = totalClicks > 0 ? Math.round((yesnoClicks / totalClicks) * 100) : 0
      const prosConsPercentage = totalClicks > 0 ? Math.round((prosConsClicks / totalClicks) * 100) : 0

      // Get recent clicks (last 10)
      const recentClicks = clicks.slice(-10).reverse()

      setResults({
        yesnoClicks,
        prosConsClicks,
        totalClicks,
        yesnoPercentage,
        prosConsPercentage,
        recentClicks
      })
    } catch (error) {
      console.error('Error loading AB test results:', error)
    }
  }

  const clearResults = () => {
    if (window.confirm('Are you sure you want to clear all AB test data?')) {
      localStorage.removeItem('abTestClicks')
      localStorage.removeItem('abTestPath')
      loadAbTestResults()
    }
  }

  const exportResults = () => {
    const clicks = JSON.parse(localStorage.getItem('abTestClicks') || '[]')
    const dataStr = JSON.stringify(clicks, null, 2)
    const dataBlob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(dataBlob)
    const link = document.createElement('a')
    link.href = url
    link.download = `ab-test-results-${new Date().toISOString().split('T')[0]}.json`
    link.click()
    URL.revokeObjectURL(url)
  }

  const getWinner = () => {
    if (results.yesnoClicks > results.prosConsClicks) {
      return 'Yes/No Picker'
    } else if (results.prosConsClicks > results.yesnoClicks) {
      return 'Pros & Cons Analysis'
    } else {
      return 'Tie'
    }
  }

  return (
    <>
      <Helmet>
        <title>AB Test Results - AI Decision Maker</title>
        <meta name="description" content="AB Test Results for Start Making Decisions button" />
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">AB Test Results</h1>
          <p className="text-gray-600">Results for "Start Making Decisions" button test</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6 border">
            <div className="flex items-center">
              <MousePointer className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Total Clicks</p>
                <p className="text-2xl font-bold text-gray-900">{results.totalClicks}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border">
            <div className="flex items-center">
              <Users className="w-8 h-8 text-green-600 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Yes/No Picker</p>
                <p className="text-2xl font-bold text-gray-900">{results.yesnoClicks}</p>
                <p className="text-sm text-gray-500">{results.yesnoPercentage}%</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border">
            <div className="flex items-center">
              <BarChart3 className="w-8 h-8 text-purple-600 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Pros & Cons</p>
                <p className="text-2xl font-bold text-gray-900">{results.prosConsClicks}</p>
                <p className="text-sm text-gray-500">{results.prosConsPercentage}%</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6 border">
            <div className="flex items-center">
              <TrendingUp className="w-8 h-8 text-orange-600 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Winner</p>
                <p className="text-lg font-bold text-gray-900">{getWinner()}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Chart */}
        <div className="bg-white rounded-xl shadow-lg p-6 border mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Click Distribution</h2>
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Yes/No Picker</span>
                <span>{results.yesnoClicks} clicks</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div 
                  className="bg-green-600 h-4 rounded-full transition-all duration-500"
                  style={{ width: `${results.yesnoPercentage}%` }}
                ></div>
              </div>
            </div>
            <div className="flex-1">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Pros & Cons</span>
                <span>{results.prosConsClicks} clicks</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div 
                  className="bg-purple-600 h-4 rounded-full transition-all duration-500"
                  style={{ width: `${results.prosConsPercentage}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Clicks */}
        <div className="bg-white rounded-xl shadow-lg p-6 border mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Clicks</h2>
          {results.recentClicks.length > 0 ? (
            <div className="space-y-3">
              {results.recentClicks.map((click, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${
                      click.path === '/tools/yesno-picker' ? 'bg-green-500' : 'bg-purple-500'
                    }`}></div>
                    <span className="font-medium">
                      {click.path === '/tools/yesno-picker' ? 'Yes/No Picker' : 'Pros & Cons'}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-500">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date(click.timestamp).toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No clicks recorded yet</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex space-x-4">
          <button
            onClick={loadAbTestResults}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Refresh Results
          </button>
          <button
            onClick={exportResults}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Export Data
          </button>
          <button
            onClick={clearResults}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Clear All Data
          </button>
        </div>
      </div>
    </>
  )
} 